import { motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { cn } from "@/lib/utils.ts";

const ease = [0.22, 1, 0.36, 1] as const;

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease },
  },
};

const sections = [
  { id: "getting-started", label: "Getting started" },
  { id: "how-it-plays", label: "How it plays" },
  { id: "exploration", label: "Exploration & discovery" },
  { id: "controls", label: "Controls" },
  { id: "system-requirements", label: "System requirements" },
  { id: "troubleshooting", label: "Troubleshooting" },
] as const;

export function DocsPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string>(sections[0].id);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { root: container, rootMargin: "-20% 0px -60% 0px", threshold: 0 },
    );

    for (const { id } of sections) {
      const el = container.querySelector(`#${id}`);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const scrollTo = useCallback((id: string) => {
    const el = scrollRef.current?.querySelector(`#${id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <section className="flex h-full w-full">
      <nav className="hidden w-52 shrink-0 border-white/8 border-r lg:block">
        <div className="sticky top-0 space-y-1 px-4 py-8">
          <p className="mb-3 font-medium text-[11px] text-foreground/40 uppercase tracking-[0.2em]">
            On this page
          </p>
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollTo(s.id)}
              className={cn(
                "block w-full rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                activeId === s.id
                  ? "bg-white/8 text-foreground"
                  : "text-foreground/50 hover:bg-white/4 hover:text-foreground/70",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-8 lg:px-10">
          <motion.div className="space-y-12" variants={stagger} initial="hidden" animate="show">
            <motion.div className="space-y-2" variants={fadeUp}>
              <Badge variant="accent">docs</Badge>
              <h1 className="font-bold text-3xl tracking-tight">Documentation</h1>
              <p className="max-w-xl text-foreground/60 text-sm leading-6">
                Everything you need to install, launch, and begin exploring the signal.
              </p>
            </motion.div>

            <motion.div id="getting-started" className="scroll-mt-8 space-y-6" variants={fadeUp}>
              <div className="space-y-1">
                <h2 className="font-semibold text-lg tracking-tight">Getting started</h2>
                <p className="text-foreground/50 text-sm">
                  From download to first session in three steps.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <StepCard
                  step="01"
                  title="Install"
                  body="Download the build for your platform from the release page and run the installer. Windows ships as an NSIS package, macOS as a DMG, and Linux as an AppImage."
                />
                <StepCard
                  step="02"
                  title="First launch"
                  body="On first run an auto-setup screen will download the inference transformers (~10–25 GB) and optimize them for your system. Let it finish — the game needs these to generate narrative and scenes."
                />
                <StepCard
                  step="03"
                  title="Updates"
                  body="Grab the latest installer from the download page and run it over your existing install. Save files are preserved between versions."
                />
              </div>
            </motion.div>

            <motion.div id="how-it-plays" className="scroll-mt-8 space-y-6" variants={fadeUp}>
              <div className="space-y-1">
                <h2 className="font-semibold text-lg tracking-tight">How it plays</h2>
                <p className="text-foreground/50 text-sm">
                  A narrative shaped by your decisions, tracked by systems running beneath the
                  surface.
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Action points</CardTitle>
                  <CardDescription>
                    Every meaningful action draws from a shared pool of 10 AP. Spend them carefully
                    — or rest to recover.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-px overflow-hidden rounded-xl border border-white/8">
                    <SpecRow
                      label="Examine"
                      value="2 AP to survey your surroundings. Re-examining a location costs 3 AP with diminishing returns."
                    />
                    <SpecRow
                      label="Interact"
                      value="1 AP to use, take, search, or activate objects you've discovered."
                    />
                    <SpecRow
                      label="Move"
                      value="Free. Travel between connected areas costs nothing."
                    />
                    <SpecRow
                      label="Talk"
                      value="Free. Conversation with anyone present carries no cost."
                    />
                    <SpecRow label="Rest" value="Skips your turn but recovers 5 AP." />
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-4 sm:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Health & survival</CardTitle>
                    <CardDescription>
                      You begin at full health. Damage is real and the narrative responds to your
                      condition.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-foreground/60 text-sm leading-6">
                    <p>
                      Health runs from 0 to 100. Hostile encounters, hazardous environments, and
                      poor choices can reduce it. Reaching zero ends the session — you'll be offered
                      a chance to reload your last save.
                    </p>
                    <p>
                      There is no passive regeneration. Recovery depends on what you find and how
                      you use it.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Inventory</CardTitle>
                    <CardDescription>
                      Carry up to 20 items. Everything you pick up has a category and a purpose.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-foreground/60 text-sm leading-6">
                    <p>
                      Items fall into five categories: tools, key items, consumables, artifacts, and
                      information. You start with a personal datapad.
                    </p>
                    <p>
                      Some items are required to progress. Others open alternative paths or reveal
                      hidden details the narrative would otherwise keep from you.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Encounters</CardTitle>
                  <CardDescription>
                    When tensions escalate, you choose how to respond.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-foreground/60 text-sm leading-6">
                  <p>
                    You can confront a threat directly, defend yourself, attempt to flee, or use an
                    item from your inventory. The outcome depends on the situation, what you're
                    carrying, and how the narrative has unfolded up to that point. The game engine
                    evaluates your action against the full context of the story.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div id="exploration" className="scroll-mt-8 space-y-6" variants={fadeUp}>
              <div className="space-y-1">
                <h2 className="font-semibold text-lg tracking-tight">Exploration & discovery</h2>
                <p className="text-foreground/50 text-sm">
                  The universe doesn't give up its secrets easily. Searching takes effort and
                  attention.
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Perception & investigation</CardTitle>
                  <CardDescription>
                    Two internal traits determine what you notice when you look around.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-foreground/60 text-sm leading-6">
                  <p>
                    <span className="font-medium text-foreground/80">Perception</span> governs what
                    you spot on a surface-level scan — obvious objects, environmental details,
                    anything in plain sight.{" "}
                    <span className="font-medium text-foreground/80">Investigation</span> determines
                    whether you uncover things that are deliberately concealed.
                  </p>
                  <p>
                    When you examine an area, both traits feed into a check against the location's
                    difficulty. Some areas are straightforward; others resist scrutiny. Re-examining
                    the same place is possible but yields less each time.
                  </p>
                  <div className="grid gap-px overflow-hidden rounded-xl border border-white/8">
                    <SpecRow
                      label="Critical"
                      value="Exceptional awareness. Everything is revealed — including anything hidden."
                    />
                    <SpecRow
                      label="Success"
                      value="You find what's obvious. Most objects and details surface."
                    />
                    <SpecRow
                      label="Partial"
                      value="A fragment. One or two things catch your eye, but you sense there's more."
                    />
                    <SpecRow
                      label="Failure"
                      value="Nothing stands out. The AP is spent regardless."
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>The universe</CardTitle>
                  <CardDescription>
                    A network of connected spaces, each with its own character and difficulty -
                    forming greater areas.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-foreground/60 text-sm leading-6">
                  <p>
                    Rooms and spaces connect to one another through defined passages; you can only
                    move where a path exists.
                  </p>
                  <p>
                    Objects within spaces range from narrative set pieces to physical items you can
                    interact with, passages to other areas, and things that simply reward closer
                    inspection. Some are hidden until you search thoroughly enough. Every object
                    tracks whether you've found it, how many times you've interacted with it, and
                    its current state.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Memory & progression</CardTitle>
                  <CardDescription>
                    The narrative accumulates. Nothing is forgotten.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-foreground/60 text-sm leading-6">
                  <p>
                    The game engine tracks your recent actions in detail, compresses older turns
                    into summaries, and maintains a set of long-term memories for pivotal moments.
                    Active objectives guide the story forward.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div id="controls" className="scroll-mt-8" variants={fadeUp}>
              <Card>
                <CardHeader>
                  <CardTitle>Controls</CardTitle>
                  <CardDescription>
                    All gameplay happens through natural language. Type what you want to do and the
                    game engine decides what happens next.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <KeyBind
                      keys={["Type"]}
                      description="Enter any action in plain language — examine, move, talk, use items, or anything you can imagine."
                    />
                    <KeyBind
                      keys={["Enter"]}
                      description="Submit your action to the game master."
                    />
                    <KeyBind keys={["/"]} description="Focus the command input while playing." />
                    <KeyBind keys={["Esc"]} description="Toggle the settings menu." />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div id="system-requirements" className="scroll-mt-8" variants={fadeUp}>
              <Card>
                <CardHeader>
                  <CardTitle>System requirements</CardTitle>
                  <CardDescription>
                    Local inference means the hardware does the thinking. An NVIDIA GPU is strongly
                    recommended.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-px overflow-hidden rounded-xl border border-white/8">
                    <SpecRow
                      label="GPU"
                      value="NVIDIA with CUDA support (RTX series recommended). Text inference uses 2–8 GB VRAM; image generation 4–8 GB."
                    />
                    <SpecRow label="RAM" value="16 GB recommended, 8 GB minimum." />
                    <SpecRow
                      label="Storage"
                      value="30–50 GB for downloaded models. The installer itself is small — transformers are fetched on first run."
                    />
                    <SpecRow label="OS" value="Windows, macOS, and Linux." />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div id="troubleshooting" className="scroll-mt-8" variants={fadeUp}>
              <div className="space-y-1 pb-2">
                <h2 className="font-semibold text-lg tracking-tight">Troubleshooting</h2>
                <p className="text-foreground/50 text-sm">
                  Check the simple things first before assuming the build is broken.
                </p>
              </div>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="models">
                  <AccordionTrigger>Transformer loading fails</AccordionTrigger>
                  <AccordionContent>
                    <p className="font-space text-foreground/68 leading-6">
                      Use the built in integrity/checksum tool. Re-download via Settings →
                      Transformers. The game engine requires UQFF format, not plain GGUF.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="performance">
                  <AccordionTrigger>Poor performance or crashes</AccordionTrigger>
                  <AccordionContent>
                    <p className="font-space text-foreground/68 leading-6">
                      This usually means insufficient VRAM. Close other GPU-heavy applications or
                      switch to a smaller transformer in Settings. CPU fallback is available but
                      roughly 5× slower.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="saves">
                  <AccordionTrigger>Corrupt or broken save files</AccordionTrigger>
                  <AccordionContent>
                    <p className="font-space text-foreground/68 leading-6">
                      Save files live in the app data folder under{" "}
                      <code className="rounded bg-white/8 px-1.5 py-0.5 text-xs">saves/</code>.
                      Delete the offending JSON file and start a new game. Saves from older versions
                      may not be forward-compatible after major updates.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, title, body }: { step: string; title: string; body: string }) {
  return (
    <motion.div
      className="rounded-2xl border border-white/8 bg-white/[0.03] p-4"
      variants={fadeUp}
      whileHover={{
        backgroundColor: "rgba(255,255,255,0.05)",
        borderColor: "rgba(255,255,255,0.14)",
        transition: { duration: 0.2 },
      }}
    >
      <span className="font-mono text-foreground/30 text-xs">{step}</span>
      <h3 className="mt-1.5 font-medium text-foreground/90">{title}</h3>
      <p className="mt-2 font-space text-foreground/55 text-sm leading-5">{body}</p>
    </motion.div>
  );
}

function KeyBind({ keys, description }: { keys: string[]; description: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-white/[0.03] px-3.5 py-3">
      <div className="flex shrink-0 gap-1 pt-0.5">
        {keys.map((key) => (
          <kbd
            key={key}
            className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-white/12 bg-white/6 px-1.5 font-mono text-foreground/70 text-xs"
          >
            {key}
          </kbd>
        ))}
      </div>
      <p className="text-foreground/60 text-sm leading-5">{description}</p>
    </div>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 bg-white/[0.025] px-4 py-3 sm:flex-row sm:items-start sm:gap-4">
      <span className="w-20 shrink-0 font-medium text-foreground/80 text-sm">{label}</span>
      <span className="text-foreground/55 text-sm leading-5">{value}</span>
    </div>
  );
}
